<template>
  <div ref="contentOrgEl" class="content-organization">
    <!-- plus free access callout -->
    <plus-free-access-callout v-if="userAuthStore.plusFreeAccess.enabled" />

    <search-header
      :title="pageTitle"
      :has-tutorial="false"
      active="collections"
      active-action="link"
      :filter-url="filterUrl"
      :browse-url="browseUrl"
      :show-tags="false"
      @tag-update="tagUpdate"
    >
      <template v-if="hasQueryParams && !isDefaultQueryParams" #customContentTags>
        <ul class="tag-actions">
          <li class="action clear">
            <icon-button
              type="button"
              icon="close"
              classes="clear"
              label="Reset"
              :reduce="false"
              :icons-outlined="false"
              @click-action="reset"
            />
          </li>
        </ul>
      </template>
    </search-header>

    <div ref="contentResultsEl" class="content-type-options">
      <search-filters
        :loading="page.loading"
        :show-keywords="false"
        :show-tags="false"
        :entity-type="entityType"
        @filter-update="filterUpdate"
      >
        <plan-browse-filters @filter-update="filterUpdate" />
      </search-filters>

      <div class="content-results">
        <!-- error state, modal will also show -->
        <callout v-if="errorMsg" type="error">
          <div>{{ errorMsg }}</div>
        </callout>

        <!-- result top bar -->
        <div class="results-details">
          <search-sort
            :total="collectionData.length"
            result-label="Collections"
            :sortable="false"
          />
        </div>

        <!-- results -->
        <content-collections
          v-if="!errorMsg && (searchResults.loading || collectionData.length)"
          v-slot="{ collectionOrder }"
          :loading="searchResults.loading"
          :collection-total="collectionData.length"
          :show-units="false"
        >
          <content-collection
            v-for="(collection, index) in collectionData"
            v-show="collection.cards.length"
            :key="collection.id"
            :title="collection.title"
            :fc-edit="false"
            :fc-delete="false"
            :fc-copy="false"
            :fc-share="false"
            :view-all="collection.url"
            :style="{ order: collectionOrder(index) }"
          >
            <plan-card v-for="plan in collection.cards" :key="plan.id" :plan="plan" />
          </content-collection>
        </content-collections>
        <div v-else class="content-group -center -vs-small">
          <search-no-results button-label="Reset" @clear-all="reset" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import SearchHeader from '@/components/search/search-header.vue'
import SearchFilters from '@/components/search/search-filters.vue'
import { useUserAuthStore } from '@/stores/user-auth'
import { computed, onBeforeMount, onBeforeUnmount, ref, shallowRef, watch } from 'vue'
import { useSearchStore } from '@/stores/search'
import { type LocationQueryValue, useRoute } from 'vue-router'
import type { InitCheckboxFilter, PlanContentCollection } from '@/types/search-types'
import { fbApi, isAbortError, isSearchParamsEqual, objToSearchParams } from '@/utils/http-api'
import PlanBrowseFilters from '@/components/plans/plan-browse-filters.vue'
import { removeArraySyntax } from '@/utils/helpers'
import ContentCollections from '@/components/search/content-collections.vue'
import Callout from '@/components/notifications/callout.vue'
import ContentCollection from '@/components/search/content-collection.vue'
import PlanCard from '@/components/plans/plan-card.vue'
import SearchNoResults from '@/components/search/search-no-results.vue'
import SearchSort from '@/components/search/search-sort.vue'
import { useSearchRoute } from '@/composables/search-route'
import IconButton from '@/components/buttons/icon-button.vue'
import PlusFreeAccessCallout from '@/components/notifications/plus-free-access-callout.vue'

/** STATE **/
const route = useRoute()
const userAuthStore = useUserAuthStore()
const searchStore = useSearchStore()
const { contentResultsEl, contentOrgEl, scrollAndFocus, routeUpdate, tagUpdate, filterUpdate } =
  useSearchRoute()

const page = ref<{
  loading: boolean
  error: string
}>({
  loading: false,
  error: '',
})

const searchResults = ref<{
  loading: boolean
  error: string
}>({
  loading: false,
  error: '',
})

const collectionData = shallowRef<PlanContentCollection[]>([])

const isMyProgramsBrowse = computed(() => route.name === 'MyProgramsBrowse')

/** NON REACTIVE **/
let fetchAbort: AbortController | null = null

const entityType = 'plan'

const checkboxFilters: Record<string, InitCheckboxFilter> = {
  schedule: {
    label: 'Schedule',
    name: 'schedule',
    items: [{ label: 'New to You', value: 'unscheduled' }],
  },
  fb: {
    label: 'FB Collections',
    name: 'fb',
    items: [
      { label: 'Trending', value: 'trending' },
      { label: 'Community Favorites', value: 'community_favorites' },
      { label: 'Pilot Programs', value: 'pilot_programs' },
    ],
  },
  picktime: {
    label: 'Pick A Time',
    name: 'picktime',
    items: [
      { label: 'About 30 Minutes', value: 'about_30_minutes' },
      { label: 'More than 30 Minutes', value: 'more_than_30_minutes' },
    ],
  },
  specialtyprogram: {
    label: 'Specialty Programs',
    name: 'specialtyprogram',
    items: [
      { label: '5 Day Trainer Series', value: 117 },
      { label: '10 Day Trainer Series', value: 118 },
      { label: 'Pregnancy and Postpartum', value: 121 },
    ],
  },
  difficulty: {
    label: 'Difficulty',
    name: 'difficulty',
    items: [
      { label: 'Beginner', value: 'beginner' },
      { label: 'Intermediate', value: 'intermediate' },
      { label: 'Advanced', value: 'advanced' },
    ],
  },
}

/** COMPUTED **/
const pageTitle = computed(() =>
  isMyProgramsBrowse.value ? 'My Workout Programs' : 'Browse Workout Programs',
)
const filterUrl = computed(() => (isMyProgramsBrowse.value ? '/my/programs' : '/plans'))
const browseUrl = computed(() =>
  isMyProgramsBrowse.value ? '/my/programs/browse' : '/plans/browse',
)
const errorMsg = computed(() => page.value.error || searchResults.value.error)

const hasQueryParams = computed(() => Object.keys(route.query).length > 0)

const isDefaultQueryParams = computed(() => {
  const defaultParams = defaultQueryParams()
  const routeParams = removeArraySyntax(route.query)

  // have to check each key, could use lodash isEqual but only dealing
  // a few arrays and string values
  return isSearchParamsEqual(routeParams, defaultParams)
})

/** WATCHERS **/
watch(
  () => route.query,
  () => {
    // resync state from url source of truth
    syncUrlToState({ init: false })
    fetchCollections()
  },
)

/** LIFECYCLE **/
onBeforeMount(async () => {
  // clear everything
  searchStore.$reset()

  // if route already has query string, use it
  if (hasQueryParams.value) {
    syncUrlToState({ init: true })
    fetchCollections()
    return
  }

  // otherwise if no query string, use defaults
  const customQueryParams = defaultQueryParams()
  syncUrlToState({ init: true, customQueryParams })
  fetchCollections(customQueryParams)
})

// cancel fetch request, especially if going to browse view as it will
// as it will use the search store
onBeforeUnmount(() => {
  if (fetchAbort) {
    fetchAbort.abort()
  }
})

/** METHODS **/
function defaultQueryParams(): Record<string, string | string[]> {
  return {
    fb: ['trending', 'community_favorites', 'pilot_programs'],
    picktime: ['about_30_minutes'],
    specialtyprogram: ['117'],
    difficulty: ['beginner'],
  }
}

/**
 * Update pinia based on url query params as the source of truth.
 * Clear out values and reset them based on the query params
 */
function syncUrlToState({
  init,
  customQueryParams,
}: {
  init: boolean
  customQueryParams?: Record<string, string | string[]>
}) {
  // removes [] and forces array for items with [] in var names
  let queryParams = removeArraySyntax(route.query)

  // override if passed in params
  if (customQueryParams) {
    queryParams = customQueryParams
  }

  // filters
  searchStore.setCheckboxFilters({
    checkboxFilters,
    queryStringFilters: queryParams,
    checkboxUrlParameterNames: searchStore.createIncludeExcludeParamMap(
      {
        schedule: 'schedule',
        fb: 'fb',
        picktime: 'picktime',
        specialtyprogram: 'specialtyprogram',
        difficulty: 'difficulty',
      },
      userAuthStore.features.VIDEO_FILTERS,
    ),
    init,
  })

  // no dropdown filters

  // no range filters

  // no keywords or sort

  // rebuild tags last, after all pinia state is loaded
  searchStore.setInitialTags()
}

async function reset() {
  await routeUpdate(defaultQueryParams())
  scrollAndFocus({ scrollTo: contentOrgEl, focusTitle: true })
}

async function fetchCollections(
  customQueryParams?: Record<string, string | string[] | LocationQueryValue | LocationQueryValue[]>,
) {
  // clear any error
  searchResults.value.error = ''
  searchResults.value.loading = true

  // treat route as source of truth unless custom params passed in for "default"
  let params: Record<string, string | string[] | LocationQueryValue | LocationQueryValue[]> =
    route.query || {}
  params = removeArraySyntax(params)

  if (customQueryParams) {
    params = customQueryParams
  }

  try {
    // cancel any previous fetch
    if (fetchAbort) {
      fetchAbort.abort()
    }

    // re-init abort controller
    fetchAbort = new AbortController()

    const apiUrl = isMyProgramsBrowse.value ? `/plans/collections/purchased` : `/plans/collections`

    const res = await fbApi.get(apiUrl, {
      searchParams: objToSearchParams(params),
      signal: fetchAbort.signal,
    })

    if (!res || !res.body || !res.body.data) {
      throw new Error(`Unable to fetch collections, please refresh the page and try again`)
    }

    const {
      collections,
    }: {
      collections: PlanContentCollection[]
    } = res.body.data

    collectionData.value = collections

    // disable facets
    searchStore.updateFacets()
  } catch (err) {
    // skip if Aborted
    if (isAbortError(err)) {
      console.warn('fetchCollections aborted')
      return
    }

    // oops model will show
    console.error(err)
    searchResults.value.error = `Unable to fetch collections, please refresh the page and try again`
  } finally {
    searchResults.value.loading = false
  }
}
</script>
